import { Button, Grid, styled } from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { calendarEntriesShiftTemplateApi, shiftApi } from "api";
import {
    ShiftEntity,
    ShiftTemplateEntity,
    UpdateShiftWithChoiceDtoChoiceEnum,
    AdditionalBreakDto,
} from "api/generated";
import AppModal from "components/AppModal";
import { H2 } from "components/Typography";
import FlexBox from "components/flexbox/FlexBox";
import AppTextField from "components/input-fields/AppTextField";
import { enGB, it } from "date-fns/locale";
import { format } from "date-fns";
import { useFormik } from "formik";
import PositionAccordion from "page-sections/shifts/positions-accordion";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ignoreUTC } from "utils/utils";
import * as Yup from "yup";
import AssignPositionToShiftModal from "./AssignPositionsToShiftModal";
import { useSeason } from "contexts/SeasonContext";
import FutureShiftUpdateModal from "./FutureShiftUpdateModal";
import { v4 as uuidv4 } from 'uuid';
import { checkTokenRole, checkCanViewShifts } from "utils/checkToken";
export interface ShiftDetailLocal {
    id: number;
    localId?: string,
    workPositionId: number;
    startTime?: string;
    endTime?: string;
    breakStart?: string | null;
    breakEnd?: string | null;
    additionalBreaks?: Array<{ id?: number; startTime: string; endTime: string; localBreakId?: string }>;
    _isDeleted?: boolean;
}

interface ModalProps {
    data?: ShiftEntity | null;
    date?: Date;
    open: boolean;
    template: ShiftTemplateEntity;
    onClose: () => void;
    fetchData: () => void;
    setData: (data: ShiftEntity | undefined) => void;
    departmentId: number;
    openedFrom?: 'shifts-table' | 'assignment-page'; // Context: where the modal was opened from
}

const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 1100,
    minWidth: 200,
    [theme.breakpoints.down(325)]: { maxWidth: "100%" },
}));

const AddShiftModal: FC<ModalProps> = ({
    fetchData,
    onClose,
    template,
    open,
    data,
    setData,
    departmentId,
    date,
    openedFrom = 'assignment-page', // Default to assignment-page for backward compatibility
}) => {
    // Defense-in-depth: viewer-only users must never interact with this write surface
    if (checkTokenRole() === 'USER' && checkCanViewShifts()) {
        onClose()
        return null
    }

    const { t } = useTranslation();
    const { seasonId } = useSeason();

    const [shiftDetails, setShiftDetails] = useState<ShiftDetailLocal[]>([]);
    const [openAssignPositionToShift, setOpenAssignPositionToShift] = useState(false);
    const [openEditOccurrenceModal, setOpenEditOccurrenceModal] = useState(false);
    const [assignments, setAssignments] = useState<ShiftEntity[]>([]);
    const [shiftNotes, setShiftNotes] = useState<string | undefined>(undefined);

    const initialValues = {
        name: "",
        startTime: new Date(),
        endTime: new Date(),
        notes: "",
    };

    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        touched,
        resetForm,
        setFieldValue,
    } = useFormik({
        initialValues,
        validationSchema: Yup.object().shape({
            name: Yup.string()
                .trim()
                .min(3, t("common.forms.field.min", { field: t("shiftTemplate.name"), min: 3 }))
                .required(t("common.forms.field.required", { field: t("shiftTemplate.name") })),
            startTime: Yup.date()
                .typeError(t("employees.agreements.validation.typeError"))
                .required(t("common.forms.field.required", { field: t("employees.agreements.field.date") })),
            endTime: Yup.date()
                .typeError(t("employees.agreements.validation.typeError"))
                .required(t("common.forms.field.required", { field: t("employees.agreements.field.date") })),
        }),
        onSubmit: async (values) => {
            if (!data) {
                await shiftApi
                    .create({
                        name: values.name,
                        startTime: ignoreUTC(values.startTime).toISOString(),
                        endTime: ignoreUTC(values.endTime).toISOString(),
                        templateId: template.id,
                        departmentId,
                        seasonId,
                        notes: shiftNotes,
                    })
                    .then(() => {
                        fetchData();
                        onClose();
                    });
            } else {
                // If opened from shifts-table, directly update without showing future modal
                if (openedFrom === 'shifts-table') {
                    await submitData('all'); // Use 'all' to update template directly
                } else {
                    // If opened from assignment page, show the future modal
                    setOpenEditOccurrenceModal(true);
                }
            }
        },
    });
    useEffect(() => {
        if (data) {
            setFieldValue("name", data.name, true);
            setFieldValue("startTime", ignoreUTC(new Date(data.startTime), true));
            setFieldValue("endTime", ignoreUTC(new Date(data.endTime), true));
            setShiftNotes(data.notes || undefined);

            const localDetails: ShiftDetailLocal[] = (data.shiftDetails ?? []).map((d) => ({
                id: d.id,
                workPositionId: d.workPositionId,
                startTime: d.startTime,
                endTime: d.endTime,
                breakStart: d.breakStart ?? null,
                breakEnd: d.breakEnd ?? null,
                additionalBreaks: (d as any).additionalBreaks?.map((ab: AdditionalBreakDto) => ({
                    id: ab.id,
                    startTime: ab.startTime,
                    endTime: ab.endTime,
                    localBreakId: ab.id ? undefined : uuidv4(), // Generate localBreakId for new breaks
                })) ?? [],
                _isDeleted: false,
            }));
            setShiftDetails(localDetails);
        } else {
            setFieldValue("name", initialValues.name, true);
            setFieldValue("startTime", initialValues.startTime, true);
            setFieldValue("endTime", initialValues.endTime, true);
            setShiftNotes(undefined);
            setShiftDetails([]);
        }
    }, [data]);

    // Separate useEffect to fetch assignments when date, data, or departmentId changes
    useEffect(() => {
        if (data && date) {
            // Format date as YYYY-MM-DD at midnight UTC to ensure correct calendar day
            const dateString = format(date, "yyyy-MM-dd");
            const dateISO = `${dateString}T00:00:00.000Z`;
            
            calendarEntriesShiftTemplateApi.findByDate(
                dateISO,
                departmentId,
                seasonId
            ).then((res) => {
                // Check if data already has shiftAssignments (from v2 direct pass)
                if (data.shiftDetails?.some(detail => detail.shiftAssignments && detail.shiftAssignments.length > 0)) {
                    // Data was passed directly from v2 with assignments already included
                    // Filter shiftAssignments to only include those for the selected date
                    const dateStr = format(date, "yyyy-MM-dd");
                    const shiftWithFilteredAssignments = {
                        ...data,
                        shiftDetails: data.shiftDetails.map(detail => {
                            if (!detail.shiftAssignments) return detail;
                            
                            const filteredAssignments = detail.shiftAssignments.filter(assignment => {
                                const assignmentDateStr = format(new Date(assignment.date), "yyyy-MM-dd");
                                return assignmentDateStr === dateStr && !assignment.deleted;
                            });
                            
                            return {
                                ...detail,
                                shiftAssignments: filteredAssignments
                            };
                        })
                    };
                    
                    setAssignments([shiftWithFilteredAssignments]);
                    return;
                }
                
                // Find shifts that match the current shift ID
                let matchingShifts = res.data.shiftTemplate?.shifts?.filter((shift) => shift.id === data?.id);
                
                // If no match by ID, try to find by name (for duplicate shifts with same name)
                if (!matchingShifts || matchingShifts.length === 0) {
                    matchingShifts = res.data.shiftTemplate?.shifts?.filter((shift) => shift.name === data?.name);
                }
                
                if (matchingShifts && matchingShifts.length > 0) {
                    // Filter shiftAssignments to only include those for the selected date
                    const dateStr = format(date, "yyyy-MM-dd");
                    const shiftsWithFilteredAssignments = matchingShifts.map(shift => {
                        if (!shift.shiftDetails) return shift;
                        
                        const filteredDetails = shift.shiftDetails.map(detail => {
                            if (!detail.shiftAssignments) return detail;
                            
                            // Filter assignments to only include those for the selected date
                            const filteredAssignments = detail.shiftAssignments.filter(assignment => {
                                const assignmentDateStr = format(new Date(assignment.date), "yyyy-MM-dd");
                                return assignmentDateStr === dateStr && !assignment.deleted;
                            });
                            
                            return {
                                ...detail,
                                shiftAssignments: filteredAssignments
                            };
                        });
                        
                        return {
                            ...shift,
                            shiftDetails: filteredDetails
                        };
                    });
                    
                    setAssignments(shiftsWithFilteredAssignments);
                } else {
                    setAssignments([]);
                }
            }).catch((err) => {
                console.error("Error fetching assignments:", err);
                setAssignments([]);
            });
        } else {
            setAssignments([]);
        }
    }, [data, date, departmentId, seasonId]);

    const handleAddResource = (workPositionId: number, resourceCount: number) => {
        const newDetails: ShiftDetailLocal[] = [];
        for (let i = 0; i < resourceCount; i++) {
            const uuid = uuidv4();
            newDetails.push({
                id: 0,
                localId: uuid,
                workPositionId,
                startTime: undefined,
                endTime: undefined,
                breakStart: null,
                breakEnd: null,
                additionalBreaks: [],
                _isDeleted: false,
            });
        }

        setShiftDetails((prev) => [...prev, ...newDetails]);
    };

    const fetchShiftAssigns = async () => {
        if (!data) return;
        const result = (await shiftApi.findShiftDetails(data.id, departmentId, seasonId)).data;
        const localDetails: ShiftDetailLocal[] = result.map((d) => ({
            id: d.id,
            workPositionId: d.workPositionId,
            startTime: d.startTime,
            endTime: d.endTime,
            breakStart: d.breakStart ?? null,
            breakEnd: d.breakEnd ?? null,
            additionalBreaks: (d as any).additionalBreaks?.map((ab: AdditionalBreakDto) => ({
                id: ab.id,
                startTime: ab.startTime,
                endTime: ab.endTime,
            })) ?? [],
            _isDeleted: false,
        }));
        setShiftDetails(localDetails);
        data.shiftDetails = result;
    };

    const submitData = async (choice?: UpdateShiftWithChoiceDtoChoiceEnum) => {
        if (!data) {
            onClose();
            return;
        }

        const finalShiftData = {
            name: values.name,
            startTime: ignoreUTC(values.startTime).toISOString(),
            endTime: ignoreUTC(values.endTime).toISOString(),
            notes: shiftNotes,
        };

        let anyError = false;
        for (const detail of shiftDetails) {
            if (!detail._isDeleted) {
                const hasBreakStart = !!detail.breakStart;
                const hasBreakEnd = !!detail.breakEnd;
                if ((hasBreakStart && !hasBreakEnd) || (!hasBreakStart && hasBreakEnd)) {
                    anyError = true;
                }
            }
        }
        if (anyError) return;

        try {
            
            // Build the array, converting undefined => null
            const detailsToSend = shiftDetails.map((d) => ({
                id: d.id,
                workPositionId: d.workPositionId,
                startTime: d.startTime ?? null,
                endTime: d.endTime ?? null,
                breakStart: d.breakStart ?? null,
                breakEnd: d.breakEnd ?? null,
                additionalBreaks: d.additionalBreaks
                    ?.filter(ab => ab.startTime && ab.endTime && ab.startTime.trim() !== '' && ab.endTime.trim() !== '') // Only include breaks with both valid times
                    .map(ab => ({
                        id: ab.id,
                        startTime: ab.startTime,
                        endTime: ab.endTime,
                    })) ?? [],
                _isDeleted: d._isDeleted ?? false,
            }));

            await shiftApi.updateShiftAndDetailsWithChoice(data.id, {
                choice: choice ?? "all", // 'single'|'all'|'future'
                date: date?.toISOString() ?? new Date().toISOString(),
                departmentId,
                seasonId,
                updateShiftDto: finalShiftData,
                updateShiftDetails: detailsToSend,
            });

            fetchData();
            onClose();
            resetForm();
        } catch (err) {
            console.error("Failed to update shift or details in one request:", err);
        }
    };
    const handleOccurrenceConfirm = async (choice: "single" | "all" | "future") => {
        await submitData(choice);
        setOpenEditOccurrenceModal(false);
    };

    const openShiftAssign = async () => {
        if (!data) {
            await shiftApi
                .create({
                    name: values.name,
                    startTime: ignoreUTC(values.startTime).toISOString(),
                    endTime: ignoreUTC(values.endTime).toISOString(),
                    templateId: template.id,
                    seasonId,
                    departmentId,
                    notes: shiftNotes,
                })
                .then((res) => {
                    fetchData();
                    setData(res.data);
                });
        }
        setOpenAssignPositionToShift(true);
    };

    const handleTimeChange = (isStart: boolean, value: Date | null) => {
        if (value && !isNaN(+value)) {
            value.setSeconds(0, 0);
            setFieldValue(isStart ? "startTime" : "endTime", value, true);
        } else {
            setFieldValue(isStart ? "startTime" : "endTime", undefined, true);
        }
    };

    const handleNotesChange = (notes: string | null) => {
        setShiftNotes(notes || undefined);
    };

    const timePicker = typeof window !== "undefined" ? document.getElementById("startTime") : null;

    return (
        <>
            <StyledAppModal open={open} handleClose={onClose}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2} alignItems={"stretch"} display={"flex"}>
                        <Grid item xs={12} display={"flex"} mb={2}>
                            <H2>
                                {data
                                    ? t("common.forms.button.edit") + " " + t("shift.itemName")
                                    : t("common.forms.addItemLabel", {
                                        item: t("shift.itemName"),
                                    })}
                            </H2>
                        </Grid>
                        <Grid item xs={4}>
                            <AppTextField
                                InputProps={{
                                    style: {
                                        height: timePicker?.clientHeight ?? 56,
                                        display: "flex",
                                        alignSelf: "flex-end",
                                    },
                                }}
                                sx={{ display: "flex" }}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                placeholder=""
                                size="small"
                                name="name"
                                label={t("shiftTemplate.name")}
                                value={values.name}
                                onChange={handleChange}
                                error={Boolean(errors.name && touched.name)}
                                helperText={(touched.name && errors.name) as string}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                                <TimePicker
                                    label={t("shift.startTime")}
                                    value={values.startTime}
                                    onChange={(val) => { handleTimeChange(true, val) }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!errors.startTime,
                                            id: "startTime",
                                            helperText: errors.startTime as string,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                                <TimePicker
                                    label={t("shift.endTime")}
                                    value={values.endTime}
                                    onChange={(val) => { handleTimeChange(false, val) }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!errors.endTime,
                                            helperText: errors.endTime as string,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                    <PositionAccordion
                        assignments={assignments}
                        data={shiftDetails}
                        setData={setShiftDetails}
                        openAddShiftDetail={openShiftAssign}
                        turnStart={values.startTime}
                        turnEnd={values.endTime}
                        departmentId={departmentId}
                        shiftNotes={shiftNotes}
                        onShiftNotesChange={handleNotesChange}
                    />
                    {data && (
                        <AssignPositionToShiftModal
                            onAddResource={handleAddResource}
                            fetchData={fetchShiftAssigns}
                            departmentId={template.departmentId}
                            shiftId={data.id}
                            open={openAssignPositionToShift}
                            onClose={() => { setOpenAssignPositionToShift(false) }}
                        />
                    )}
                    <FlexBox justifyContent="flex-end" gap={2} marginTop={4}>
                        <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                onClose();
                                resetForm();
                            }}
                        >
                            {t("common.forms.button.cancel")}
                        </Button>
                        <Button fullWidth size="small" type="submit" variant="contained">
                            {t("common.forms.button.save")}
                        </Button>
                    </FlexBox>
                </form>
            </StyledAppModal>
            <FutureShiftUpdateModal
                open={openEditOccurrenceModal}
                selectedDate={date}
                onClose={() => { setOpenEditOccurrenceModal(false) }}
                onConfirm={handleOccurrenceConfirm}
            />
        </>
    );
};

export default AddShiftModal;
